// lib/types/languages.ts

export type LanguageOption = {
  code: string
  label: string
}

/**
 * Language codes use ISO 639-1 where possible. Regional and script variants
 * use BCP-47 (e.g. zh-CN, zh-TW, fr-CA, pt-BR, sr-Latn).
 *
 * `code` is the value sent to the backend / WebSocket on login and stored on
 * device groups. `label` is the native name with the English name in parens,
 * matching the mobile app's Appearance > Language list so the portal and
 * device stay visually consistent.
 *
 * Sorted alphabetically by English name.
 */
export const LANGUAGES: LanguageOption[] = [
  { code: 'sq', label: 'Shqip (Albanian)' },
  { code: 'ar', label: 'العربية (Arabic)' },
  { code: 'bn', label: 'বাংলা (Bengali)' },
  { code: 'bs', label: 'Bosanski (Bosnian)' },
  { code: 'bg', label: 'Български (Bulgarian)' },
  { code: 'ca', label: 'Català (Catalan)' },
  { code: 'zh-CN', label: '简体中文 (Simplified Chinese)' },
  { code: 'zh-TW', label: '繁體中文 (Traditional Chinese)' },
  { code: 'hr', label: 'Hrvatski (Croatian)' },
  { code: 'cs', label: 'Čeština (Czech)' },
  { code: 'da', label: 'Dansk (Danish)' },
  { code: 'nl', label: 'Nederlands (Dutch)' },
  { code: 'en', label: 'English' },
  { code: 'et', label: 'Eesti (Estonian)' },
  { code: 'fi', label: 'Suomi (Finnish)' },
  { code: 'fr', label: 'Français (French)' },
  { code: 'fr-CA', label: 'Français (French) - Canada' },
  { code: 'gl', label: 'Galego (Galician)' },
  { code: 'ka', label: 'ქართული (Georgian)' },
  { code: 'de', label: 'Deutsch (German)' },
  { code: 'el', label: 'Ελληνικά (Greek)' },
  { code: 'gu', label: 'ગુજરાતી (Gujarati)' },
  { code: 'he', label: 'עברית (Hebrew)' },
  { code: 'hi', label: 'हिन्दी (Hindi)' },
  { code: 'hu', label: 'Magyar (Hungarian)' },
  { code: 'is', label: 'Íslenska (Icelandic)' },
  { code: 'id', label: 'Bahasa Indonesia (Indonesian)' },
  { code: 'it', label: 'Italiano (Italian)' },
  { code: 'ja', label: '日本語 (Japanese)' },
  { code: 'kn', label: 'ಕನ್ನಡ (Kannada)' },
  { code: 'km', label: 'ខ្មែរ (Khmer)' },
  { code: 'ko', label: '한국어 (Korean)' },
  { code: 'lv', label: 'Latviešu (Latvian)' },
  { code: 'lt', label: 'Lietuvių (Lithuanian)' },
  { code: 'ms', label: 'Melayu (Malay)' },
  { code: 'ml', label: 'മലയാളം (Malayalam)' },
  { code: 'mr', label: 'मराठी (Marathi)' },
  { code: 'ne', label: 'नेपाली (Nepali)' },
  { code: 'nb', label: 'Norsk (Norwegian)' },
  { code: 'pl', label: 'Polski (Polish)' },
  { code: 'pt', label: 'Português (Portuguese)' },
  { code: 'pt-BR', label: 'Português do Brasil (Portuguese - Brazil)' },
  { code: 'ro', label: 'Română (Romanian)' },
  { code: 'ru', label: 'Русский (Russian)' },
  { code: 'sr', label: 'Српски (Serbian)' },
  { code: 'sr-Latn', label: 'Srpski (Serbian - Latin)' },
  { code: 'si', label: 'සිංහල (Sinhala)' },
  { code: 'sk', label: 'Slovenčina (Slovak)' },
  { code: 'es', label: 'Español (Spanish)' },
  { code: 'sw', label: 'Kiswahili (Swahili)' },
  { code: 'sv', label: 'Svenska (Swedish)' },
  { code: 'ta', label: 'தமிழ் (Tamil)' },
  { code: 'te', label: 'తెలుగు (Telugu)' },
  { code: 'th', label: 'ไทย (Thai)' },
  { code: 'tr', label: 'Türkçe (Turkish)' },
  { code: 'uk', label: 'Українська (Ukrainian)' },
  { code: 'ur', label: 'اردو (Urdu)' },
  { code: 'vi', label: 'Tiếng Việt (Vietnamese)' },
  { code: 'cy', label: 'Cymraeg (Welsh)' },
]

/** Convenience map for quick lookups */
export const LANGUAGE_LABEL_BY_CODE: Record<string, string> = Object.fromEntries(
  LANGUAGES.map(l => [l.code, l.label])
)
